import React, { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft, FaPlus, FaTrash } from "react-icons/fa";

import {
  DetailSectionSkeleton,
  GalleryPreviewGrid,
  PageHeader,
  ScheduleCalendar,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
} from "components/admin";
import { CrudForm, type CrudFieldConfig } from "components/admin/workspace-crud";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import RightDrawer from "components/global/RightDrawer";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { DataTable } from "components/molecules/table/table";
import {
  buildEventCreateValues,
  buildEventFields,
  buildEventUpdateValues,
  buildPlayerAchievementCreateValues,
  buildPlayerAchievementFields,
  buildPlayerAchievementUpdateValues,
  buildPlayerCareerCreateValues,
  buildPlayerCareerFields,
  buildPlayerCareerUpdateValues,
  buildPlayerPositionPlayedCreateValues,
  buildPlayerPositionPlayedFields,
  buildPlayerPositionPlayedUpdateValues,
  buildPlayerSeasonStatCreateValues,
  buildPlayerSeasonStatFields,
  buildPlayerSeasonStatUpdateValues,
} from "pages/app/workspace/sections/forms/athlete-section-forms";
import adminApi from "services/admin-api";
import { formatDate, getApiErrorMessage } from "utils/admin";
import {
  canAccessPortalPath,
  canDeleteGlobalAthletes,
  canManageOwnedAthleteEvents,
  hasPortalAction,
  hasPortalCapability,
  isAthletePortalOwner,
} from "utils/portal-access";
import {
  AthleteProfileBioText,
  AthleteProfileContentStack,
  AthleteProfileContentStackSecondary,
  AthleteProfileDangerButton,
  AthleteProfileGridLayout,
  AthleteProfileGridLayoutQuaternary,
  AthleteProfileGridLayoutSecondary,
  AthleteProfileHeroImage,
  AthleteProfileIdentityName,
  AthleteProfileIdentityRole,
  AthleteProfileIdentityRow,
  AthleteProfileMetaText,
  AthleteProfileOverviewDetails,
  AthleteProfileOverviewLabel,
  AthleteProfileOverviewLayout,
  AthleteProfileOverviewRow,
  AthleteProfileOverviewValue,
  AthleteProfileSecondaryButton,
  AthleteProfileSectionActionButton,
  AthleteProfileSectionTertiary,
  AthleteProfileTeamButton,
  AthleteProfileTeamMeta,
  AthleteProfileTeamName,
  AthleteProfileTeamsList,
  AthleteProfileTeamText,
} from "pages/app/athletes/styles/athlete-profile.page.inline.styles";

type EditableAthleteSection =
  | "career"
  | "season-stats"
  | "positions-played"
  | "achievements"
  | "calendar";

type EditorState = {
  mode: "create" | "edit";
  row: any | null;
  section: EditableAthleteSection;
};

const sectionTitles: Record<EditableAthleteSection, string> = {
  achievements: "achievement",
  calendar: "event",
  career: "career entry",
  "positions-played": "position entry",
  "season-stats": "season stat",
};

const AthleteProfilePage: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const { portalAccess } = useAuth();

  const { data: athlete, isLoading } = useQuery({
    queryKey: ["athlete", id],
    queryFn: () => adminApi.athletes.get(id),
    enabled: Boolean(id),
  });
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const effectivePortalAccess = currentUser?.portal_access || portalAccess;
  const canOpenGlobalTeams = canAccessPortalPath("/teams", effectivePortalAccess);
  const isAthleteOwner =
    String(currentUser?.player?.id || "") === String(id) &&
    isAthletePortalOwner(currentUser, effectivePortalAccess);
  const canManageAthleteRecord =
    currentUser?.role === "admin" ||
    hasPortalAction(effectivePortalAccess, "players.manage") ||
    (isAthleteOwner &&
      hasPortalCapability(effectivePortalAccess, "can_manage_own_player_profile"));
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminApi.teams.list(),
    enabled: Boolean(id) && (canManageAthleteRecord || canOpenGlobalTeams),
  });
  const { data: sports = [] } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
    enabled: Boolean(id),
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: () => adminApi.countries.list(),
    enabled: Boolean(id),
  });
  const calendarQuery = useQuery({
    queryKey: ["athlete-calendar", id],
    queryFn: () => adminApi.athletes.getCalendarEvents(id),
    enabled: Boolean(id),
  });

  const calendarItems = useMemo(
    () =>
      (calendarQuery.data || []).map((event: any, index: number) => ({
        category: "event" as const,
        details: event.details || event.description || "No event details provided yet.",
        id: event.id || `athlete-event-${index}`,
        location: event.location || event.venue_name || "Location pending",
        start: event.start_date || event.start || event.date || event.scheduled_at,
        end:
          event.end_date ||
          event.end ||
          event.date ||
          event.scheduled_at ||
          event.start_date ||
          event.start,
        status: event.status,
        title: event.name || event.title || "Untitled event",
      })),
    [calendarQuery.data],
  );

  const careerFields = useMemo(
    () => buildPlayerCareerFields({ countries, sports, teams }),
    [countries, sports, teams],
  );
  const seasonStatFields = useMemo(
    () => buildPlayerSeasonStatFields({ sports, teams }),
    [sports, teams],
  );
  const positionFields = useMemo(
    () => buildPlayerPositionPlayedFields({ sports, teams }),
    [sports, teams],
  );
  const achievementFields = useMemo(
    () => buildPlayerAchievementFields({ sports, teams }),
    [sports, teams],
  );
  const eventFields = useMemo(
    () => buildEventFields([athlete]).filter((field) => field.name !== "player_id"),
    [athlete],
  );

  const editorFields: Record<EditableAthleteSection, CrudFieldConfig[]> = {
    achievements: achievementFields,
    calendar: eventFields,
    career: careerFields,
    "positions-played": positionFields,
    "season-stats": seasonStatFields,
  };
  const canManageAthleteCalendar =
    currentUser?.role === "admin" ||
    hasPortalAction(effectivePortalAccess, "events.manage") ||
    (isAthleteOwner && canManageOwnedAthleteEvents(currentUser, effectivePortalAccess));
  const canDeleteAthlete = canDeleteGlobalAthletes(
    effectivePortalAccess,
    currentUser?.role || null,
  );

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!canDeleteAthlete) {
        return Promise.reject(new Error("You do not have permission to delete this athlete."));
      }

      return adminApi.athletes.remove(id);
    },
    onSuccess: () => {
      toast.success("Athlete removed.");
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      history.push("/athletes");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to remove this athlete.");
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (!editorState) {
        return null;
      }

      switch (editorState.section) {
        case "career":
          return editorState.mode === "edit"
            ? adminApi.playerCareers.update(id, editorState.row.id, payload)
            : adminApi.playerCareers.create(id, payload);
        case "season-stats":
          return editorState.mode === "edit"
            ? adminApi.playerSeasonStats.update(id, editorState.row.id, payload)
            : adminApi.playerSeasonStats.create(id, payload);
        case "positions-played":
          return editorState.mode === "edit"
            ? adminApi.playerPositionsPlayed.update(id, editorState.row.id, payload)
            : adminApi.playerPositionsPlayed.create(id, payload);
        case "achievements":
          return editorState.mode === "edit"
            ? adminApi.playerAchievements.update(id, editorState.row.id, payload)
            : adminApi.playerAchievements.create(id, payload);
        case "calendar":
          return editorState.mode === "edit"
            ? adminApi.events.update(editorState.row.id, payload)
            : adminApi.events.create({
                ...payload,
                player_id: id,
              });
        default:
          return null;
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["athlete", id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["athlete-calendar", id],
        }),
      ]);
      toast.success("Athlete section updated.");
      setEditorState(null);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this athlete record."));
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async ({ row, section }: { row: any; section: EditableAthleteSection }) => {
      switch (section) {
        case "career":
          return adminApi.playerCareers.remove(id, row.id);
        case "season-stats":
          return adminApi.playerSeasonStats.remove(id, row.id);
        case "positions-played":
          return adminApi.playerPositionsPlayed.remove(id, row.id);
        case "achievements":
          return adminApi.playerAchievements.remove(id, row.id);
        case "calendar":
          return adminApi.events.remove(row.id);
        default:
          return null;
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["athlete", id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["athlete-calendar", id],
        }),
      ]);
      toast.success("Athlete record removed.");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this athlete record."));
    },
  });

  const athleteTeams = athlete?.team_names_and_positions || [];

  const getInitialValues = (state: EditorState | null) => {
    if (!state) {
      return {};
    }

    switch (state.section) {
      case "career":
        return state.mode === "edit"
          ? buildPlayerCareerUpdateValues(state.row)
          : buildPlayerCareerCreateValues();
      case "season-stats":
        return state.mode === "edit"
          ? buildPlayerSeasonStatUpdateValues(state.row)
          : buildPlayerSeasonStatCreateValues();
      case "positions-played":
        return state.mode === "edit"
          ? buildPlayerPositionPlayedUpdateValues(state.row)
          : buildPlayerPositionPlayedCreateValues();
      case "achievements":
        return state.mode === "edit"
          ? buildPlayerAchievementUpdateValues(state.row)
          : buildPlayerAchievementCreateValues();
      case "calendar":
        return state.mode === "edit"
          ? buildEventUpdateValues({ player: athlete }, state.row)
          : buildEventCreateValues(String(id));
      default:
        return {};
    }
  };

  const openCreateDrawer = (section: EditableAthleteSection) =>
    setEditorState({
      mode: "create",
      row: null,
      section,
    });

  const openEditDrawer = (section: EditableAthleteSection, row: any) =>
    setEditorState({
      mode: "edit",
      row,
      section,
    });

  const handleRemove = (section: EditableAthleteSection, row: any) => {
    if (!window.confirm("Remove this record? This action cannot be undone.")) {
      return;
    }

    deleteSectionMutation.mutate({
      row,
      section,
    });
  };

  return (
    <AppLayout route="/athletes">
      <PageHeader
        title={athlete?.full_name || "Athlete profile"}
        description="Review the full athlete profile including bio, career history, season output, positions, achievements, and calendar records."
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "Athletes",
            to: "/athletes",
          },
          {
            label: athlete?.full_name || "Athlete",
          },
        ]}
        actions={
          <>
            <AthleteProfileSecondaryButton onClick={() => history.push("/athletes")} type="button">
              <FaArrowLeft size={12} />
              Back
            </AthleteProfileSecondaryButton>
            {canDeleteAthlete ? (
              <AthleteProfileDangerButton
                onClick={() => {
                  if (window.confirm("Delete this athlete? This action cannot be undone.")) {
                    deleteMutation.mutate();
                  }
                }}
                type="button"
              >
                <FaTrash size={12} />
                Delete
              </AthleteProfileDangerButton>
            ) : null}
          </>
        }
      />

      {isLoading ? (
        <StatsGridSkeleton />
      ) : (
        <AthleteProfileGridLayout>
          <StatCard label="Position" value={athlete?.position || "Unknown"} />
          <StatCard label="Followers" value={athlete?.follower_count ?? 0} />
          <StatCard label="Career Games" value={athlete?.total_career_games ?? 0} />
          <StatCard label="Career Points" value={athlete?.total_career_points ?? 0} />
        </AthleteProfileGridLayout>
      )}

      <AthleteProfileGridLayoutSecondary>
        <SectionCard subtitle="Bio, body metrics, and current team alignment" title="Profile">
          {isLoading ? (
            <DetailSectionSkeleton imageClassName="h-32 w-32 max-w-full sm:h-44 sm:w-44" />
          ) : (
            <AthleteProfileContentStack>
              <AthleteProfileOverviewLayout>
                <AthleteProfileIdentityRow>
                  <AthleteProfileHeroImage
                    alt={athlete?.full_name || "Athlete"}
                    fallbackLabel={athlete?.full_name}
                    src={athlete?.avatar_url || athlete?.image}
                    variant="athlete"
                  />
                  <AthleteProfileOverviewDetails>
                    <AthleteProfileIdentityName>
                      {athlete?.full_name || "Unnamed athlete"}
                    </AthleteProfileIdentityName>
                    <AthleteProfileIdentityRole>
                      {athlete?.position || "Position pending"}
                    </AthleteProfileIdentityRole>
                  </AthleteProfileOverviewDetails>
                </AthleteProfileIdentityRow>

                <AthleteProfileBioText>
                  {athlete?.profile?.bio || "No bio is currently available for this athlete."}
                </AthleteProfileBioText>

                <AthleteProfileOverviewDetails>
                  <AthleteProfileOverviewRow>
                    <AthleteProfileOverviewLabel>Teams</AthleteProfileOverviewLabel>
                    <AthleteProfileOverviewValue>
                      {athleteTeams.length ? (
                        <AthleteProfileTeamsList>
                          {athleteTeams.map((team: any) => (
                            <AthleteProfileTeamButton
                              key={team.id || team.team_name}
                              disabled={!canOpenGlobalTeams || !team.id}
                              onClick={() => {
                                if (!canOpenGlobalTeams || !team.id) {
                                  return;
                                }

                                history.push(`/teams/${team.id}`);
                              }}
                              type="button"
                            >
                              <AthleteProfileTeamText>
                                <AthleteProfileTeamName>
                                  {team.team_name || "Unnamed team"}
                                </AthleteProfileTeamName>
                                <AthleteProfileTeamMeta>
                                  {team.position || athlete?.position || "Position pending"}
                                </AthleteProfileTeamMeta>
                              </AthleteProfileTeamText>
                            </AthleteProfileTeamButton>
                          ))}
                        </AthleteProfileTeamsList>
                      ) : (
                        "Unassigned"
                      )}
                    </AthleteProfileOverviewValue>
                  </AthleteProfileOverviewRow>
                  <AthleteProfileOverviewRow>
                    <AthleteProfileOverviewLabel>Sports</AthleteProfileOverviewLabel>
                    <AthleteProfileOverviewValue>
                      {(athlete?.sport_names || []).join(", ") || "Unknown"}
                    </AthleteProfileOverviewValue>
                  </AthleteProfileOverviewRow>
                  <AthleteProfileOverviewRow>
                    <AthleteProfileOverviewLabel>Country</AthleteProfileOverviewLabel>
                    <AthleteProfileOverviewValue>
                      {athlete?.profile?.country || athlete?.country || "Unknown"}
                    </AthleteProfileOverviewValue>
                  </AthleteProfileOverviewRow>
                  <AthleteProfileOverviewRow>
                    <AthleteProfileOverviewLabel>Physical</AthleteProfileOverviewLabel>
                    <AthleteProfileOverviewValue>
                      {athlete?.profile?.height || "?"} cm / {athlete?.profile?.weight || "?"} kg
                    </AthleteProfileOverviewValue>
                  </AthleteProfileOverviewRow>
                </AthleteProfileOverviewDetails>
              </AthleteProfileOverviewLayout>
            </AthleteProfileContentStack>
          )}
        </SectionCard>

        <AthleteProfileContentStackSecondary>
          <SectionCard
            action={
              canManageAthleteRecord ? (
                <AthleteProfileSectionActionButton
                  onClick={() => openCreateDrawer("career")}
                  type="button"
                >
                  <FaPlus size={12} />
                  Add career
                </AthleteProfileSectionActionButton>
              ) : undefined
            }
            subtitle="Career timeline records"
            title="Career"
          >
            <DataTable
              canEdit={canManageAthleteRecord}
              canRemove={canManageAthleteRecord}
              columns={[
                {
                  label: "Team",
                  accessor: (row: any) => row.team_name || "Unknown",
                },
                {
                  label: "League",
                  accessor: (row: any) => row.league_name || "Not set",
                },
                {
                  label: "Country",
                  accessor: (row: any) => row.country_name || "Unknown",
                },
                {
                  label: "Start",
                  accessor: (row: any) => row.start_year || "N/A",
                },
              ]}
              data={athlete?.player_careers || []}
              hideNav
              onEdit={canManageAthleteRecord ? (row: any) => openEditDrawer("career", row) : undefined}
              onRemove={canManageAthleteRecord ? (_id, row) => row && handleRemove("career", row) : undefined}
            />
          </SectionCard>

          <SectionCard
            action={
              canManageAthleteRecord ? (
                <AthleteProfileSectionActionButton
                  onClick={() => openCreateDrawer("season-stats")}
                  type="button"
                >
                  <FaPlus size={12} />
                  Add season stat
                </AthleteProfileSectionActionButton>
              ) : undefined
            }
            subtitle="Season output and usage"
            title="Season Stats"
          >
            <DataTable
              canEdit={canManageAthleteRecord}
              canRemove={canManageAthleteRecord}
              columns={[
                {
                  label: "Season",
                  accessor: (row: any) => row.season || "N/A",
                },
                {
                  label: "Tournament",
                  accessor: (row: any) => row.tournament || "Unknown",
                },
                {
                  label: "Matches",
                  accessor: (row: any) => row.matches_played ?? 0,
                },
                {
                  label: "Wins",
                  accessor: (row: any) => row.wins ?? 0,
                },
                {
                  label: "Points",
                  accessor: (row: any) => row.points ?? 0,
                },
              ]}
              data={athlete?.player_season_stats || []}
              hideNav
              onEdit={canManageAthleteRecord ? (row: any) => openEditDrawer("season-stats", row) : undefined}
              onRemove={canManageAthleteRecord ? (_id, row) => row && handleRemove("season-stats", row) : undefined}
            />
          </SectionCard>
        </AthleteProfileContentStackSecondary>
      </AthleteProfileGridLayoutSecondary>

      <AthleteProfileGridLayoutQuaternary>
        <SectionCard
          action={
            canManageAthleteRecord ? (
              <AthleteProfileSectionActionButton
                onClick={() => openCreateDrawer("positions-played")}
                type="button"
              >
                <FaPlus size={12} />
                Add position
              </AthleteProfileSectionActionButton>
            ) : undefined
          }
          subtitle="Position split by season"
          title="Positions Played"
        >
          <DataTable
            canEdit={canManageAthleteRecord}
            canRemove={canManageAthleteRecord}
            columns={[
              {
                label: "Position",
                accessor: (row: any) => row.position || "Unknown",
              },
              {
                label: "Appearances",
                accessor: (row: any) => row.appearances ?? 0,
              },
              {
                label: "Share",
                accessor: (row: any) => `${row.percentage || 0}%`,
              },
              {
                label: "Season",
                accessor: (row: any) => row.season || "N/A",
              },
            ]}
            data={athlete?.player_positions_played || []}
            hideNav
            onEdit={canManageAthleteRecord ? (row: any) => openEditDrawer("positions-played", row) : undefined}
            onRemove={canManageAthleteRecord ? (_id, row) => row && handleRemove("positions-played", row) : undefined}
          />
        </SectionCard>

        <SectionCard
          action={
            canManageAthleteRecord ? (
              <AthleteProfileSectionActionButton
                onClick={() => openCreateDrawer("achievements")}
                type="button"
              >
                <FaPlus size={12} />
                Add achievement
              </AthleteProfileSectionActionButton>
            ) : undefined
          }
          subtitle="Awards and standout moments"
          title="Achievements"
        >
          <DataTable
            canEdit={canManageAthleteRecord}
            canRemove={canManageAthleteRecord}
            columns={[
              {
                label: "Title",
                accessor: (row: any) => row.title || "Untitled",
              },
              {
                label: "Season",
                accessor: (row: any) => row.season || row.year || "N/A",
              },
              {
                label: "Tournament",
                accessor: (row: any) => row.tournament || "Unknown",
              },
              {
                label: "Level",
                accessor: (row: any) => row.level || "Unspecified",
              },
            ]}
            data={athlete?.achievements || []}
            hideNav
            onEdit={canManageAthleteRecord ? (row: any) => openEditDrawer("achievements", row) : undefined}
            onRemove={canManageAthleteRecord ? (_id, row) => row && handleRemove("achievements", row) : undefined}
          />
        </SectionCard>
      </AthleteProfileGridLayoutQuaternary>

      <AthleteProfileSectionTertiary>
        <SectionCard
          action={
            canManageAthleteCalendar ? (
              <AthleteProfileSectionActionButton
                onClick={() => openCreateDrawer("calendar")}
                type="button"
              >
                <FaPlus size={12} />
                Add event
              </AthleteProfileSectionActionButton>
            ) : undefined
          }
          subtitle="Upcoming appearances, sessions, and player events on a proper calendar view."
          title="Calendar"
        >
          <ScheduleCalendar
            emptyDescription="Player calendar items will appear here once events exist."
            emptyTitle="No calendar items"
            isLoading={calendarQuery.isLoading}
            items={calendarItems}
          />
          <div className="mt-6">
            <DataTable
              canEdit={canManageAthleteCalendar}
              canRemove={canManageAthleteCalendar}
              columns={[
                {
                  label: "Event",
                  accessor: (row: any) => row.name || row.title || "Untitled event",
                },
                {
                  label: "Date",
                  accessor: (row: any) => row.date || "Unknown",
                },
                {
                  label: "Location",
                  accessor: (row: any) => row.location || "Location pending",
                },
              ]}
              data={calendarQuery.data || []}
              hideNav
              isLoading={calendarQuery.isLoading}
              onEdit={canManageAthleteCalendar ? (row: any) => openEditDrawer("calendar", row) : undefined}
              onRemove={canManageAthleteCalendar ? (_id, row) => row && handleRemove("calendar", row) : undefined}
            />
          </div>
        </SectionCard>
      </AthleteProfileSectionTertiary>

      <AthleteProfileSectionTertiary>
        <SectionCard
          subtitle="The updated athlete profile now includes a dedicated gallery collection."
          title="Gallery"
        >
          <GalleryPreviewGrid
            emptyDescription="No athlete gallery items have been added yet."
            emptyTitle="No athlete gallery"
            items={athlete?.gallery_items || []}
          />
        </SectionCard>
      </AthleteProfileSectionTertiary>

      {athlete?.created_at ? (
        <AthleteProfileMetaText>
          Athlete profile created {formatDate(athlete.created_at)} and last updated{" "}
          {formatDate(athlete.updated_at)}.
        </AthleteProfileMetaText>
      ) : null}

      <RightDrawer
        isOpen={Boolean(editorState)}
        onClose={() => setEditorState(null)}
        title={
          editorState
            ? `${editorState.mode === "edit" ? "Edit" : "Add"} ${sectionTitles[editorState.section]}`
            : "Edit athlete detail"
        }
      >
        {editorState ? (
          <CrudForm
            fields={editorFields[editorState.section]}
            initialValues={getInitialValues(editorState)}
            isSubmitting={saveMutation.isPending}
            onCancel={() => setEditorState(null)}
            onSubmit={(payload) => saveMutation.mutate(payload)}
            submitLabel={editorState.mode === "edit" ? "Save changes" : "Create record"}
          />
        ) : null}
      </RightDrawer>
    </AppLayout>
  );
};

export default AthleteProfilePage;
